import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import toast, { Toaster } from "react-hot-toast";
import "../css/builder.css";
import Navbar from "../components/navbar";
import ResumeDocument from "../components/resumethemes";

const formatSkills = (skillsData) => {
  let arr = [];
  if (Array.isArray(skillsData)) arr = [...skillsData];
  else if (typeof skillsData === "string")
    arr = skillsData
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s);
  if (arr.length === 0 || arr[arr.length - 1] !== "") arr.push("");
  return arr;
};

const formatBullets = (descData) => {
  let arr = [];
  if (Array.isArray(descData)) arr = [...descData];
  else if (typeof descData === "string")
    arr = descData
      .split(/\n|•|- /)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  if (arr.length === 0 || arr[arr.length - 1] !== "") arr.push("");
  return arr;
};

const createEmptyFormData = () => ({
  personal: {
    name: "",
    role: "",
    email: "",
    phone: "",
    location: "",
    picture: null,
    customLinks: [],
  },
  summary: "",
  experience: [],
  education: [],
  projects: [],
  skills: [""],
});

// Checks if a CV form state is completely blank
const isFormDataEmpty = (data) => {
  if (!data) return true;
  const p = data.personal || {};
  const hasPersonal = Boolean(
    p.name?.trim() ||
    p.role?.trim() ||
    p.email?.trim() ||
    p.phone?.trim() ||
    p.location?.trim() ||
    p.picture ||
    (Array.isArray(p.customLinks) &&
      p.customLinks.some((l) => l.label?.trim() || l.url?.trim())),
  );
  const hasSummary = Boolean(data.summary?.trim());
  const hasExp =
    Array.isArray(data.experience) &&
    data.experience.some(
      (e) =>
        e.company?.trim() ||
        e.role?.trim() ||
        e.dates?.trim() ||
        (Array.isArray(e.desc) && e.desc.some((b) => b?.trim())),
    );
  const hasEdu =
    Array.isArray(data.education) &&
    data.education.some(
      (ed) =>
        ed.school?.trim() || ed.degree?.trim() || ed.qualifications?.trim(),
    );
  const hasProj =
    Array.isArray(data.projects) &&
    data.projects.some((pr) => pr.title?.trim() || pr.desc?.trim());
  const hasSkills =
    Array.isArray(data.skills) && data.skills.some((s) => s?.trim());

  return !(
    hasPersonal ||
    hasSummary ||
    hasExp ||
    hasEdu ||
    hasProj ||
    hasSkills
  );
};

// Safely normalises any parsed draft object
const normaliseFormData = (raw) => {
  const base = createEmptyFormData();
  if (!raw || typeof raw !== "object") return base;
  return {
    ...base,
    ...raw,
    personal: {
      ...base.personal,
      ...(raw.personal || {}),
      customLinks: Array.isArray(raw.personal?.customLinks)
        ? raw.personal.customLinks
        : [],
    },
    summary: typeof raw.summary === "string" ? raw.summary : "",
    experience: Array.isArray(raw.experience)
      ? raw.experience.map((exp) => ({
          ...exp,
          id: exp.id || Date.now() + Math.random(),
          desc: formatBullets(exp.desc),
        }))
      : [],
    education: Array.isArray(raw.education) ? raw.education : [],
    projects: Array.isArray(raw.projects) ? raw.projects : [],
    skills: formatSkills(raw.skills),
  };
};

// Live ATS Score & Actionable Fixes Analyser
const calculateAtsAnalysis = (formData) => {
  let score = 0;
  const fixes = [];
  const p = formData?.personal || {};

  // 1. Name & Job Title (+15%)
  if (p.name?.trim() && p.role?.trim()) {
    score += 15;
  } else {
    fixes.push({
      id: "personal-role",
      label: "Add your name & job title",
      boost: "+15%",
      points: 15,
      section: "personal",
    });
  }

  // 2. Contact Details (+10%)
  if (p.email?.trim() && p.phone?.trim()) {
    score += 10;
  } else {
    fixes.push({
      id: "personal-contact",
      label: "Add your email & phone number",
      boost: "+10%",
      points: 10,
      section: "personal",
    });
  }

  // 3. Professional Summary (+15%)
  if (formData?.summary && formData.summary.trim().length >= 25) {
    score += 15;
  } else {
    fixes.push({
      id: "summary",
      label: "Add a summary",
      boost: "+15%",
      points: 15,
      section: "summary",
    });
  }

  // 4. Work Experience Role (+20%)
  const validExp = (formData?.experience || []).filter(
    (e) => e.company?.trim() || e.role?.trim(),
  );
  if (validExp.length > 0) {
    score += 20;
  } else {
    fixes.push({
      id: "experience-role",
      label: "Add work experience",
      boost: "+20%",
      points: 20,
      section: "experience",
    });
  }

  // 5. Work Experience Bullet Points (+10%)
  const bulletCount = (formData?.experience || []).reduce((acc, exp) => {
    const bullets = Array.isArray(exp.desc)
      ? exp.desc.filter((b) => b?.trim().length > 0)
      : [];
    return acc + bullets.length;
  }, 0);
  if (bulletCount >= 2) {
    score += 10;
  } else {
    fixes.push({
      id: "experience-bullets",
      label: "Add achievement bullets to experience",
      boost: "+10%",
      points: 10,
      section: "experience",
    });
  }

  // 6. Education (+15%)
  const validEdu = (formData?.education || []).filter(
    (ed) => ed.school?.trim() || ed.degree?.trim() || ed.qualifications?.trim(),
  );
  if (validEdu.length > 0) {
    score += 15;
  } else {
    fixes.push({
      id: "education",
      label: "Add education history",
      boost: "+15%",
      points: 15,
      section: "education",
    });
  }

  // 7. Skills (+15%)
  const validSkills = (formData?.skills || []).filter(
    (s) => s?.trim().length > 0,
  );
  if (validSkills.length >= 3) {
    score += 15;
  } else {
    fixes.push({
      id: "skills",
      label: "Add at least 3 key skills",
      boost: "+15%",
      points: 15,
      section: "skills",
    });
  }

  // Sort fixes by highest point impact first
  fixes.sort((a, b) => b.points - a.points);

  return { score: Math.min(100, score), fixes };
};

// Define component outside to prevent re-rendering unmount bugs
const ColorPickerRow = ({
  label,
  propName,
  palette,
  currentValue,
  onColorChange,
}) => (
  <div className="design-group">
    <label>{label}</label>
    <div
      className="color-swatch-container"
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "8px",
      }}
    >
      {palette.map((color) => (
        <div
          key={color}
          className={`color-swatch ${currentValue === color ? "active" : ""}`}
          style={{
            background: color,
            border:
              color === "#ffffff"
                ? "1px solid #cbd5e1"
                : "1px solid rgba(0,0,0,0.1)",
          }}
          onClick={() => onColorChange(propName, color)}
        />
      ))}

      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          cursor: "pointer",
          margin: 0,
          fontSize: "0.85rem",
          color: "#64748b",
        }}
      >
        <div
          className="color-swatch"
          style={{
            background: "transparent",
            position: "relative",
            overflow: "hidden",
            border: "1px dashed #94a3b8",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <input
            type="color"
            value={currentValue || "#000000"}
            onChange={(e) => onColorChange(propName, e.target.value)}
            style={{
              position: "absolute",
              top: "-10px",
              left: "-10px",
              width: "200%",
              height: "200%",
              cursor: "pointer",
              opacity: 0,
            }}
          />
          <span style={{ fontSize: "16px", pointerEvents: "none" }}>⚙️</span>
        </div>
        Pick your colour
      </label>
    </div>
  </div>
);

export default function ResumeBuilder() {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState("content");
  const [designSubTab, setDesignSubTab] = useState("templates");

  // Open Personal Info, Summary, and Work Experience by default so AI tools are immediately visible
  const [expandedSections, setExpandedSections] = useState([
    "personal",
    "summary",
    "experience",
  ]);

  const [zoom, setZoom] = useState(100);
  const [isGenerating, setIsGenerating] = useState(false);
  const [mobileView, setMobileView] = useState("edit");
  const [atsScore, setAtsScore] = useState(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [pdfCredits, setPdfCredits] = useState(0);
  const [isPro, setIsPro] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showMobileActions, setShowMobileActions] = useState(false);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [aiTone, setAiTone] = useState("professional");
  const [hasReachedLimit, setHasReachedLimit] = useState(false);
  const [editingExpId, setEditingExpId] = useState(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const [isMobileMode, setIsMobileMode] = useState(
    typeof window !== "undefined" ? window.innerWidth < 850 : false,
  );
  const [docHeight, setDocHeight] = useState(1131);

  // Modals
  const [upgradeModal, setUpgradeModal] = useState({
    show: false,
    title: "",
    desc: "",
  });
  const [customAlert, setCustomAlert] = useState({
    show: false,
    title: "",
    message: "",
  });
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showDownloadConfirm, setShowDownloadConfirm] = useState(false);

  const [saveAction, setSaveAction] = useState("exit");
  const [resumeTitle, setResumeTitle] = useState(
    () => localStorage.getItem("resumeDraftTitle") || "My CV",
  );
  const [resumeId, setResumeId] = useState(
    () => localStorage.getItem("resumeDraftId") || null,
  );
  const [isExporting, setIsExporting] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [isSavingPending, setIsSavingPending] = useState(false);
  const [draggedItemIndex, setDraggedItemIndex] = useState(null);

  // Tracks whether initial draft hydration has completed before autosaving
  const hasHydratedDraft = useRef(false);

  const [design, setDesign] = useState(() => {
    const initialDesign = {
      theme: "apex",
      color: "#2563eb",
      headingColor: "#0f172a",
      textColor: "#334155",
      subtitleColor: "#64748b",
      backgroundColor: "#ffffff",
      font: "Inter",
      fontSize: 8.5,
      lineHeight: 1.5,
      padding: 24,
      sectionSpacing: 12,
      itemSpacing: 8,
      headerAlign: "default",
      textTransform: "uppercase",
      showDividers: true,
      sectionOrder: [
        "personal",
        "summary",
        "experience",
        "education",
        "projects",
        "skills",
      ],
    };

    if (location.state?.isEdit) {
      const cloudDesign =
        location.state.design || location.state.resumeData?.design;
      if (cloudDesign) return { ...initialDesign, ...cloudDesign };
    }

    const savedDesign = localStorage.getItem("designDraft");
    if (savedDesign) {
      try {
        return { ...initialDesign, ...JSON.parse(savedDesign) };
      } catch (e) {
        console.error("Failed to parse local design draft");
      }
    }

    return initialDesign;
  });

  const [formData, setFormData] = useState(() => {
    // 1. If explicitly editing a non-empty cloud CV from Dashboard, load it first
    if (location.state?.isEdit && location.state?.resumeData) {
      try {
        const passed = normaliseFormData(
          JSON.parse(JSON.stringify(location.state.resumeData)),
        );
        if (!isFormDataEmpty(passed)) {
          return passed;
        }
      } catch (e) {
        console.error(e);
      }
    }

    // 2. Otherwise always load the local resumeDraft so nav hopping never loses work
    const savedForm = localStorage.getItem("resumeDraft");
    if (savedForm) {
      try {
        const parsed = normaliseFormData(JSON.parse(savedForm));
        if (!isFormDataEmpty(parsed)) {
          return parsed;
        }
      } catch (e) {
        console.error("Failed to parse local form draft");
      }
    }

    return createEmptyFormData();
  });

  // Compute live ATS score and missing section fixes
  const atsAnalysis = useMemo(() => calculateAtsAnalysis(formData), [formData]);
  const displayAtsScore = atsAnalysis.score;

  // --- UNDO / REDO STATE & LOGIC ---
  const [past, setPast] = useState([]);
  const [future, setFuture] = useState([]);
  const isUndoRedo = useRef(false);
  const lastSavedState = useRef({ formData, design });

  // 1. Debounced history tracker
  useEffect(() => {
    if (isUndoRedo.current) {
      isUndoRedo.current = false;
      return;
    }

    const timer = setTimeout(() => {
      const currentString = JSON.stringify({ formData, design });
      const lastString = JSON.stringify(lastSavedState.current);

      if (currentString !== lastString) {
        setPast((prev) => [...prev.slice(-25), lastSavedState.current]);
        lastSavedState.current = { formData, design };
        setFuture([]);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [formData, design]);

  // 2. Functional updates to guarantee we process rapid consecutive clicks correctly
  const handleUndo = useCallback(() => {
    setPast((prevPast) => {
      if (prevPast.length === 0) return prevPast;

      const previous = prevPast[prevPast.length - 1];
      const newPast = prevPast.slice(0, -1);

      setFuture((prevFuture) => [lastSavedState.current, ...prevFuture]);

      isUndoRedo.current = true;
      lastSavedState.current = previous;
      setFormData(previous.formData);
      setDesign(previous.design);

      return newPast;
    });
  }, []);

  const handleRedo = useCallback(() => {
    setFuture((prevFuture) => {
      if (prevFuture.length === 0) return prevFuture;

      const next = prevFuture[0];
      const newFuture = prevFuture.slice(1);

      setPast((prevPast) => [...prevPast, lastSavedState.current]);

      isUndoRedo.current = true;
      lastSavedState.current = next;
      setFormData(next.formData);
      setDesign(next.design);

      return newFuture;
    });
  }, []);

  // 3. Keyboard Shortcuts Listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      const activeTag = document.activeElement?.tagName?.toLowerCase();
      const isInput =
        activeTag === "input" ||
        activeTag === "textarea" ||
        document.activeElement?.isContentEditable;

      if (isInput) return;

      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;

      if (cmdOrCtrl && e.key.toLowerCase() === "z") {
        if (e.shiftKey) {
          e.preventDefault();
          handleRedo();
        } else {
          e.preventDefault();
          handleUndo();
        }
      } else if (cmdOrCtrl && e.key.toLowerCase() === "y") {
        e.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleUndo, handleRedo]);

  const API_URL =
    process.env.NODE_ENV === "production"
      ? "https://resumify.co.uk"
      : "http://localhost:5000";

  // --- UI Auto-Resize Helper Functions ---
  const handleAutoResize = (e) => {
    e.target.style.height = "auto";
    e.target.style.height = e.target.scrollHeight + "px";
  };

  const handleRefResize = (el) => {
    if (el) {
      el.style.height = "auto";
      el.style.height = el.scrollHeight + "px";
    }
  };

  useEffect(() => {
    const fetchUserStatus = async () => {
      const localUser = localStorage.getItem("user");
      if (!localUser) {
        setIsLoggedIn(false);
        setIsLoadingAuth(false);
        navigate("/auth");
        return;
      }
      try {
        const res = await fetch(`${API_URL}/api/user/credits`, {
          credentials: "include",
        });

        if (!res.ok) {
          localStorage.removeItem("user");
          setIsLoggedIn(false);
          navigate("/auth");
          return;
        }

        const data = await res.json();
        setIsLoggedIn(true);
        setPdfCredits(Number(data.credits) || 0);
        setIsPro(Boolean(data.isPro));

        if (location.state?.isEdit) {
          const incomingId = location.state.resumeId || null;
          const incomingTitle = location.state.title || "My CV";
          setResumeId(incomingId);
          setResumeTitle(incomingTitle);
          if (incomingId) localStorage.setItem("resumeDraftId", incomingId);
          localStorage.setItem("resumeDraftTitle", incomingTitle);

          const passedData = location.state.resumeData;
          if (passedData) {
            const normalisedPassed = normaliseFormData(passedData);
            // Only overwrite state if passedData actually has content
            if (!isFormDataEmpty(normalisedPassed)) {
              setFormData(normalisedPassed);
            }
          }

          const passedDesign =
            location.state.design || location.state.resumeData?.design;
          if (passedDesign) {
            setDesign((prev) => ({ ...prev, ...passedDesign }));
          }
          return;
        }

        if (location.state?.isNew) {
          setResumeId(null);
          setResumeTitle("My CV");
          localStorage.removeItem("resumeDraftId");
          localStorage.setItem("resumeDraftTitle", "My CV");
          return;
        }
      } catch (e) {
        toast.error("Error verifying account status.");
        setIsLoggedIn(false);
      } finally {
        setIsLoadingAuth(false);
      }
    };
    fetchUserStatus();
  }, [navigate, API_URL, location.state]);

  // Protected localStorage autosave: never overwrite an existing non-empty draft with an empty default
  useEffect(() => {
    if (!hasHydratedDraft.current) {
      hasHydratedDraft.current = true;
      // Still save on first run if formData actually has content
      if (isFormDataEmpty(formData)) return;
    }

    if (isFormDataEmpty(formData)) {
      const existingRaw = localStorage.getItem("resumeDraft");
      if (existingRaw) {
        try {
          const existingParsed = JSON.parse(existingRaw);
          if (!isFormDataEmpty(existingParsed)) {
            // Refill state from existing draft instead of wiping it
            setFormData(normaliseFormData(existingParsed));
            return;
          }
        } catch (e) {
          // Ignore malformed JSON
        }
      }
    }

    localStorage.setItem("resumeDraft", JSON.stringify(formData));
    localStorage.setItem("designDraft", JSON.stringify(design));
    setIsSavingPending(true);
  }, [formData, design]);

  useEffect(() => {
    // Stop auto-saving if limit is reached
    if (
      !resumeId ||
      !isLoggedIn ||
      hasReachedLimit ||
      isFormDataEmpty(formData)
    )
      return;

    setSaveStatus("Saving...");

    const autoSaveTimer = setTimeout(async () => {
      try {
        const response = await fetch(`${API_URL}/api/resumes/save`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            resumeId,
            formData,
            title: resumeTitle || "My CV",
            design,
            isAutoSave: true,
          }),
        });

        const data = await response.json();

        if (response.ok) {
          if (typeof data.atsScore === "number") setAtsScore(data.atsScore);
          const time = new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          });
          setSaveStatus(`Saved to Cloud at ${time} ✓`);
          setIsSavingPending(false);
        } else {
          if (
            response.status === 403 ||
            (data.message && data.message.toLowerCase().includes("limit"))
          ) {
            setSaveStatus("Limit Reached ⚠️");
            setHasReachedLimit(true);
            setShowSubscribeModal(true);
          }
          throw new Error("Save rejected by server");
        }
      } catch (error) {
        if (!hasReachedLimit) setSaveStatus("Save Failed ❌");
      }
    }, 1500);

    return () => clearTimeout(autoSaveTimer);
  }, [
    formData,
    design,
    resumeId,
    resumeTitle,
    API_URL,
    isLoggedIn,
    hasReachedLimit,
  ]);

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 850;
      setIsMobileMode(isMobile);

      if (isMobile) {
        const calculatedZoom = Math.floor(
          ((window.innerWidth - 40) / 800) * 100,
        );
        setZoom(calculatedZoom > 30 ? calculatedZoom : 30);
      } else {
        setZoom(100);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const paginate = () => {
      const screenDoc = document.querySelector(".scale-wrapper .a4-document");
      const wrapper = document.querySelector(".visual-doc-wrapper");

      if (!screenDoc || !wrapper) return;

      screenDoc.style.position = "relative";
      const allElements = Array.from(screenDoc.querySelectorAll("*"));

      allElements.forEach((el) => (el.style.marginTop = ""));
      screenDoc.style.height = "auto";
      wrapper.style.height = "auto";

      if (isExporting) {
        return;
      }

      const breakableElements = allElements.filter((el) => {
        const style = window.getComputedStyle(el);
        const isBlock =
          style.display === "block" ||
          style.display === "list-item" ||
          style.display === "flex" ||
          style.display === "grid";

        const hasBlockChildren = Array.from(el.children).some((child) => {
          const cStyle = window.getComputedStyle(child);
          return (
            cStyle.display === "block" ||
            cStyle.display === "list-item" ||
            cStyle.display === "flex" ||
            cStyle.display === "grid"
          );
        });
        return isBlock && !hasBlockChildren;
      });

      const PAGE_HEIGHT = 1131;
      const GAP = 40;
      const TOTAL_STEP = PAGE_HEIGHT + GAP;
      const PADDING_TOP = (design.padding || 24) + 40;
      const SAFE_BOTTOM = (design.padding || 24) + 30;

      requestAnimationFrame(() => {
        if (!document.contains(screenDoc)) return;

        breakableElements.forEach((el) => {
          let offset = 0;
          let curr = el;
          while (curr && curr !== screenDoc) {
            offset += curr.offsetTop;
            curr = curr.offsetParent;
          }

          const top = offset;
          const bottom = top + el.offsetHeight;
          const pageOfTop = Math.floor(top / TOTAL_STEP);
          const currentSectionBottom =
            pageOfTop * TOTAL_STEP + PAGE_HEIGHT - SAFE_BOTTOM;

          if (bottom > currentSectionBottom && el.offsetHeight < PAGE_HEIGHT) {
            const nextPageTop = (pageOfTop + 1) * TOTAL_STEP;
            const pushAmount = nextPageTop - top + PADDING_TOP;
            const currentMargin =
              parseFloat(window.getComputedStyle(el).marginTop) || 0;
            el.style.marginTop = `${currentMargin + pushAmount}px`;
          }
        });

        const scrollHeight = screenDoc.scrollHeight;
        let pages = 1;
        while (scrollHeight > pages * PAGE_HEIGHT + (pages - 1) * GAP) {
          pages++;
        }

        const totalDocHeight = pages * PAGE_HEIGHT + (pages - 1) * GAP;
        const finalHeight = `${totalDocHeight}px`;

        screenDoc.style.setProperty("height", finalHeight, "important");
        wrapper.style.setProperty("height", finalHeight, "important");

        setDocHeight((prev) => {
          if (prev !== totalDocHeight) {
            setTimeout(paginate, 50);
            return totalDocHeight;
          }
          return prev;
        });
      });
    };

    const timer = setTimeout(paginate, 150);
    return () => clearTimeout(timer);
  }, [formData, design, isExporting, activeTab, zoom, mobileView]);

  const handlePersonal = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      personal: { ...prev.personal, [name]: value },
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image size must be less than 2MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData((prev) => ({
        ...prev,
        personal: { ...prev.personal, picture: reader.result },
      }));
    };
    reader.readAsDataURL(file);
  };

  const removeProfilePicture = (e) => {
    e.stopPropagation();
    setFormData((prev) => ({
      ...prev,
      personal: { ...prev.personal, picture: null },
    }));
  };

  const addCustomLink = () => {
    setFormData((prev) => ({
      ...prev,
      personal: {
        ...prev.personal,
        customLinks: [
          ...(prev.personal.customLinks || []),
          { label: "", url: "" },
        ],
      },
    }));
  };

  const updateCustomLink = (index, field, value) => {
    setFormData((prev) => {
      const currentLinks = prev.personal.customLinks || [];
      const updatedLinks = currentLinks.map((link, i) => {
        if (i === index) return { ...link, [field]: value };
        return link;
      });
      return {
        ...prev,
        personal: { ...prev.personal, customLinks: updatedLinks },
      };
    });
  };

  const removeCustomLink = (index) => {
    setFormData((prev) => ({
      ...prev,
      personal: {
        ...prev.personal,
        customLinks: (prev.personal.customLinks || []).filter(
          (_, i) => i !== index,
        ),
      },
    }));
  };

  const handleDesign = (key, value) => setDesign({ ...design, [key]: value });

  const toggleAccordion = (section) => {
    setExpandedSections((prev) =>
      prev.includes(section)
        ? prev.filter((s) => s !== section)
        : [...prev, section],
    );
  };

  const jumpToSection = (sectionId) => {
    setActiveTab("content");
    setExpandedSections((prev) =>
      prev.includes(sectionId) ? prev : [...prev, sectionId],
    );
    setTimeout(() => {
      const el = document.getElementById(`accordion-${sectionId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 80);
  };

  // EXPERIENCE LOGIC
  const addExperience = () => {
    const newId = Date.now();
    setFormData((prev) => ({
      ...prev,
      experience: [
        ...prev.experience,
        { id: newId, company: "", role: "", dates: "", desc: [""] },
      ],
    }));
    setEditingExpId(newId);
  };

  const removeExperience = (id) =>
    setFormData((prev) => ({
      ...prev,
      experience: prev.experience.filter((exp) => exp.id !== id),
    }));

  const updateExperience = (id, field, value) =>
    setFormData((prev) => ({
      ...prev,
      experience: prev.experience.map((exp) =>
        exp.id === id ? { ...exp, [field]: value } : exp,
      ),
    }));

  const handleExpBulletChange = (expId, index, value) => {
    setFormData((prev) => {
      const updatedExp = prev.experience.map((exp) => {
        if (exp.id === expId) {
          const newDesc = Array.isArray(exp.desc) ? [...exp.desc] : [""];
          newDesc[index] = value;
          if (index === newDesc.length - 1 && value.trim() !== "")
            newDesc.push("");
          return { ...exp, desc: newDesc };
        }
        return exp;
      });
      return { ...prev, experience: updatedExp };
    });
  };

  const removeExpBullet = (expId, index) => {
    setFormData((prev) => {
      const updatedExp = prev.experience.map((exp) => {
        if (exp.id === expId) {
          let newDesc = exp.desc.filter((_, i) => i !== index);
          if (newDesc.length === 0) newDesc.push("");
          return { ...exp, desc: newDesc };
        }
        return exp;
      });
      return { ...prev, experience: updatedExp };
    });
  };

  // EDUCATION LOGIC
  const addEducation = () =>
    setFormData((prev) => ({
      ...prev,
      education: [
        ...prev.education,
        {
          id: Date.now(),
          type: "university",
          school: "",
          degree: "",
          qualifications: "",
          year: "",
        },
      ],
    }));

  const removeEducation = (id) =>
    setFormData((prev) => ({
      ...prev,
      education: prev.education.filter((ed) => ed.id !== id),
    }));

  const updateEducation = (id, field, value) =>
    setFormData((prev) => ({
      ...prev,
      education: prev.education.map((ed) =>
        ed.id === id ? { ...ed, [field]: value } : ed,
      ),
    }));

  // PROJECTS LOGIC
  const addProject = () =>
    setFormData((prev) => ({
      ...prev,
      projects: [
        ...prev.projects,
        { id: Date.now(), title: "", link: "", desc: "" },
      ],
    }));

  const removeProject = (id) =>
    setFormData((prev) => ({
      ...prev,
      projects: prev.projects.filter((pr) => pr.id !== id),
    }));

  const updateProject = (id, field, value) =>
    setFormData((prev) => ({
      ...prev,
      projects: prev.projects.map((pr) =>
        pr.id === id ? { ...pr, [field]: value } : pr,
      ),
    }));

  const handleSkillChange = (index, value) => {
    setFormData((prev) => {
      const newSkills = [...prev.skills];
      newSkills[index] = value;
      if (index === newSkills.length - 1 && value.trim() !== "")
        newSkills.push("");
      return { ...prev, skills: newSkills };
    });
  };

  const removeSkill = (index) =>
    setFormData((prev) => {
      const newSkills = prev.skills.filter((_, i) => i !== index);
      if (newSkills.length === 0) newSkills.push("");
      return { ...prev, skills: newSkills };
    });

  // AI HANDLERS
  const handleAutoWriteSummary = async () => {
    if (!formData.personal.role && !formData.summary?.trim()) {
      toast(
        "Please enter a Job Title or draft text first so the AI has context.",
        {
          icon: "ℹ️",
        },
      );
      return;
    }
    setIsGenerating(true);
    const toastId = toast.loading("Generating your summary...");
    try {
      const context = {
        role: formData.personal.role,
        currentSummary: formData.summary,
        skills: formData.skills.filter((s) => s.trim() !== ""),
        experience: formData.experience.map((e) => `${e.role} at ${e.company}`),
      };
      const res = await fetch(`${API_URL}/api/ai/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ type: "summary", context, tone: aiTone }),
      });
      const data = await res.json();
      if (res.ok) {
        setFormData((prev) => ({ ...prev, summary: data.result }));
        toast.success("Summary generated!", { id: toastId });
      } else {
        toast.error(data.message || "Failed to generate summary.", {
          id: toastId,
        });
      }
    } catch (error) {
      toast.error("Network error while generating AI content.", {
        id: toastId,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleOptimizeBullets = async (expId, currentExp) => {
    if (!currentExp.role || !currentExp.company) {
      toast("Please fill in the Company and Job Title first.", { icon: "ℹ️" });
      return;
    }
    setIsGenerating(true);
    const toastId = toast.loading("Optimizing impact...");
    try {
      const context = {
        role: currentExp.role,
        company: currentExp.company,
        currentBullets: (Array.isArray(currentExp.desc)
          ? currentExp.desc
          : []
        ).filter((b) => b.trim() !== ""),
      };
      const res = await fetch(`${API_URL}/api/ai/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ type: "bullets", context, tone: aiTone }),
      });
      const data = await res.json();
      if (res.ok) {
        const newBullets = formatBullets(data.result);
        setFormData((prev) => ({
          ...prev,
          experience: prev.experience.map((exp) =>
            exp.id === expId ? { ...exp, desc: newBullets } : exp,
          ),
        }));
        toast.success("Bullets optimized!", { id: toastId });
      } else {
        toast.error(data.message || "Failed to optimize bullets.", {
          id: toastId,
        });
      }
    } catch (error) {
      toast.error("Network error while generating AI content.", {
        id: toastId,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // CLOUD & FILE HANDLERS
  const handleImportExternalCV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsImporting(true);
    const toastId = toast.loading("Parsing CV... This may take a moment.");

    const formPayload = new FormData();
    formPayload.append("cvFile", file);

    try {
      const response = await fetch(`${API_URL}/api/parse-cv`, {
        method: "POST",
        body: formPayload,
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.message || "Failed to parse the CV.", { id: toastId });
        return;
      }

      const incoming = data.parsedResume || {};

      const parsedExperience = (incoming.experience || []).map((exp) => ({
        ...exp,
        id: exp.id || Date.now() + Math.random(),
        desc: formatBullets(exp.desc),
      }));

      const parsedEducation = (incoming.education || []).map((ed) => ({
        ...ed,
        id: ed.id || Date.now() + Math.random(),
      }));

      const parsedProjects = (incoming.projects || []).map((pr) => ({
        ...pr,
        id: pr.id || Date.now() + Math.random(),
      }));

      setFormData((prev) => ({
        ...prev,
        ...incoming,
        personal: { ...prev.personal, ...(incoming.personal || {}) },
        summary: incoming.summary || "",
        _rawText: data.rawText,
        experience: parsedExperience,
        education: parsedEducation,
        projects: parsedProjects,
        skills: incoming.skills
          ? formatSkills(incoming.skills)
          : formatSkills(prev.skills),
      }));

      if (data.importedFromBuilder && data.design)
        setDesign((prev) => ({ ...prev, ...data.design }));
      if (data.importedFromBuilder && data.title) setResumeTitle(data.title);

      toast.success(
        data.importedFromBuilder
          ? "Builder CV imported successfully!"
          : "CV successfully imported!",
        { id: toastId },
      );
    } catch (error) {
      console.error(error);
      toast.error("Network error. Couldn't import CV.", { id: toastId });
    } finally {
      setIsImporting(false);
      e.target.value = null;
    }
  };

  const handleCheckout = async (planType) => {
    const checkoutToastId = toast.loading("Preparing checkout...");
    try {
      await fetch(`${API_URL}/api/resumes/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          resumeId,
          formData,
          title: resumeTitle || "My Resume",
          design,
        }),
      });
    } catch (err) {
      console.error("Failed to auto-save before redirecting", err);
    }

    try {
      const response = await fetch(`${API_URL}/api/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan: planType }),
        credentials: "include",
      });

      const data = await response.json();
      if (response.status === 401) {
        window.location.href = "/auth";
        return;
      }
      if (data.url) {
        toast.dismiss(checkoutToastId);
        window.location.href = data.url;
      } else {
        toast.dismiss(checkoutToastId);
        setCustomAlert({
          show: true,
          title: "Checkout Failed",
          message: "Failed to start the checkout process.",
        });
      }
    } catch (error) {
      toast.dismiss(checkoutToastId);
      setCustomAlert({
        show: true,
        title: "Network Error",
        message: "Couldn't connect to the payment provider.",
      });
    }
  };

  const initiateSaveToCloud = (actionType = "save") => {
    setSaveAction(actionType);
    if (!resumeId) setShowSaveModal(true);
    else executeCloudSave(actionType);
  };

  const confirmSaveToCloud = () => {
    if (!resumeTitle || resumeTitle.trim() === "") {
      toast("Please enter a name for your resume.", { icon: "⚠️" });
      return;
    }
    setShowSaveModal(false);
    executeCloudSave(saveAction);
  };

  const executeCloudSave = async (action) => {
    const saveToastId = toast.loading(
      action === "exit" ? "Saving before exit..." : "Saving to cloud...",
    );
    try {
      const response = await fetch(`${API_URL}/api/resumes/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          resumeId,
          formData,
          title: resumeTitle.trim() || "My CV",
          design,
          isAutoSave: false,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success("Saved successfully!", { id: saveToastId });
        setIsSavingPending(false);
        if (data.id) {
          setResumeId(data.id);
          localStorage.setItem("resumeDraftId", data.id);
        }
        localStorage.setItem("resumeDraftTitle", resumeTitle.trim() || "My CV");
        if (typeof data.atsScore === "number") setAtsScore(data.atsScore);

        if (action === "exit") navigate("/dashboard");
        else {
          const time = new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          });
          setSaveStatus(`Saved to Cloud at ${time} ✓`);
        }
      } else {
        if (
          response.status === 403 ||
          (data.message && data.message.toLowerCase().includes("limit"))
        ) {
          toast.dismiss(saveToastId);
          setHasReachedLimit(true);
          setShowSubscribeModal(true);
        } else {
          toast.error(data.message || "Failed to save.", { id: saveToastId });
        }
      }
    } catch (error) {
      toast.error("Network error. Could not save.", { id: saveToastId });
    }
  };

  const handleDownloadClick = () => {
    if (!isLoggedIn) {
      window.location.href = "/auth";
      return;
    }

    const safeCredits = Number(pdfCredits) || 0;

    if (!isPro && safeCredits <= 0) {
      setUpgradeModal({
        show: true,
        title: "Get PDF Credits",
        desc: "Purchase a credit to download your high-quality, ATS-optimized CV.",
      });
      return;
    }

    if (!isPro && safeCredits > 0) {
      setShowDownloadConfirm(true);
    } else {
      executeDownload();
    }
  };

  const handleClearCVClick = () => {
    setShowClearConfirm(true);
  };

  const executeClearCV = () => {
    const empty = createEmptyFormData();
    localStorage.removeItem("resumeDraft");
    localStorage.removeItem("resumeDraftId");
    localStorage.removeItem("resumeDraftTitle");
    setResumeId(null);
    setResumeTitle("My CV");
    setFormData(empty);
    setShowClearConfirm(false);
    toast.success("CV content cleared!");
  };

  const executeDownload = async () => {
    setShowDownloadConfirm(false);
    setIsExporting(true);

    const previousZoom = zoom;
    setZoom(100);

    toast.success("Ready! Select 'Save as PDF' as your Destination.", {
      duration: 6000,
      icon: "💡",
    });

    const handleAfterPrint = async () => {
      setIsExporting(false);
      setZoom(previousZoom);

      window.removeEventListener("afterprint", handleAfterPrint);
      if (!isPro) {
        try {
          const res = await fetch(`${API_URL}/api/consume-credit`, {
            method: "POST",
            credentials: "include",
          });
          const data = await res.json();
          if (res.ok) setPdfCredits(Number(data.remaining) || 0);
        } catch (err) {
          console.error("Error consuming credit:", err);
        }
      }
    };

    window.addEventListener("afterprint", handleAfterPrint);
    setTimeout(() => {
      window.print();
    }, 800);
  };

  // DRAG & DROP LOGIC
  const handleDragStart = (e, index) => {
    setDraggedItemIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target.parentNode);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (draggedItemIndex === null || draggedItemIndex === targetIndex) return;
    setDesign((prev) => {
      const currentOrder = prev.sectionOrder || [
        "personal",
        "summary",
        "experience",
        "education",
        "projects",
        "skills",
      ];
      const draggables = currentOrder.filter((id) => id !== "personal");
      const draggedItem = draggables[draggedItemIndex];
      draggables.splice(draggedItemIndex, 1);
      draggables.splice(targetIndex, 0, draggedItem);
      return { ...prev, sectionOrder: ["personal", ...draggables] };
    });
    setDraggedItemIndex(null);
  };
  const handleDragEnd = () => setDraggedItemIndex(null);

  // ==========================================
  // PANEL RENDERS
  // ==========================================

  const renderContentPanel = () => {
    const sectionConfig = {
      summary: { icon: "📝", title: "Professional Summary" },
      experience: { icon: "💼", title: "Work Experience" },
      education: { icon: "🎓", title: "Education" },
      projects: { icon: "🚀", title: "Projects" },
      skills: { icon: "⚡", title: "Skills" },
    };

    const currentOrder = design.sectionOrder || [
      "personal",
      "summary",
      "experience",
      "education",
      "projects",
      "skills",
    ];
    const draggableSections = currentOrder.filter((id) => id !== "personal");
    const isPersonalOpen = expandedSections.includes("personal");

    return (
      <div className="panel-content fade-in">
        <div className="panel-header">
          <h2>Content Editor</h2>
          <p>Fill out your details below.</p>
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginTop: "12px",
              alignItems: "center",
            }}
          >
            <span
              style={{
                fontSize: "0.85rem",
                color: "#64748b",
                fontWeight: "600",
              }}
            >
              History:
            </span>
            <button
              onClick={handleUndo}
              disabled={past.length === 0}
              className="add-btn small"
              style={{
                opacity: past.length === 0 ? 0.5 : 1,
                background: "#f8fafc",
                color: "#0f172a",
                border: "1px solid #cbd5e1",
                padding: "4px 10px",
              }}
            >
              ↩️ Undo
            </button>
            <button
              onClick={handleRedo}
              disabled={future.length === 0}
              className="add-btn small"
              style={{
                opacity: future.length === 0 ? 0.5 : 1,
                background: "#f8fafc",
                color: "#0f172a",
                border: "1px solid #cbd5e1",
                padding: "4px 10px",
              }}
            >
              ↪️ Redo
            </button>
          </div>
          <div style={{ marginTop: "20px", marginBottom: "15px" }}>
            <span
              style={{
                fontSize: "0.85rem",
                color: "#64748b",
                fontWeight: "600",
                display: "block",
                marginBottom: "8px",
              }}
            >
              🤖 AI Writing Style
            </span>
            <div
              style={{
                display: "flex",
                background: "#f1f5f9",
                padding: "4px",
                borderRadius: "8px",
                gap: "4px",
              }}
            >
              {["professional", "confident", "concise"].map((tone) => (
                <button
                  key={tone}
                  onClick={() => setAiTone(tone)}
                  style={{
                    flex: 1,
                    padding: "8px 10px",
                    borderRadius: "6px",
                    border: "none",
                    background: aiTone === tone ? "#ffffff" : "transparent",
                    color: aiTone === tone ? "#0f172a" : "#64748b",
                    fontWeight: aiTone === tone ? "600" : "400",
                    boxShadow:
                      aiTone === tone ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                    cursor: "pointer",
                    textTransform: "capitalize",
                    fontSize: "0.85rem",
                    transition: "all 0.2s ease",
                  }}
                >
                  {tone}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div
          id="accordion-personal"
          className={`accordion ${isPersonalOpen ? "open" : ""}`}
        >
          <div
            className="accordion-header"
            onClick={() => toggleAccordion("personal")}
          >
            <div className="accordion-title-container">
              <span>👤 Personal Info</span>
            </div>
            <span className="chevron">{isPersonalOpen ? "▲" : "▼"}</span>
          </div>

          <div className="accordion-body">
            <div style={{ marginBottom: "15px" }}>
              <label className="form-label">Profile Picture</label>
              <div className="profile-pic-container">
                {formData.personal.picture ? (
                  <div style={{ position: "relative" }}>
                    <img
                      src={formData.personal.picture}
                      alt="Profile"
                      className="profile-pic-preview"
                    />
                    <button
                      onClick={removeProfilePicture}
                      className="remove-pic-btn"
                      title="Remove Photo"
                    >
                      ❌
                    </button>
                  </div>
                ) : (
                  <label className="upload-pic-label">
                    📷 Upload Photo
                    <input
                      type="file"
                      accept="image/png, image/jpeg, image/jpg"
                      style={{ display: "none" }}
                      onChange={handleImageUpload}
                    />
                  </label>
                )}
                <div className="upload-pic-hint">
                  Optional. Max 2MB. Recommended for international or creative
                  roles.
                </div>
              </div>
            </div>

            <input
              type="text"
              name="name"
              value={formData.personal.name}
              onChange={handlePersonal}
              placeholder="Full Name"
              className="form-input"
            />
            <input
              type="text"
              name="role"
              value={formData.personal.role}
              onChange={handlePersonal}
              placeholder="Job Title"
              className="form-input"
            />
            <div className="input-row">
              <input
                type="email"
                name="email"
                value={formData.personal.email}
                onChange={handlePersonal}
                placeholder="Email"
                className="form-input"
              />
              <input
                type="text"
                name="phone"
                value={formData.personal.phone}
                onChange={handlePersonal}
                placeholder="Phone"
                className="form-input"
              />
            </div>
            <input
              type="text"
              name="location"
              value={formData.personal.location}
              onChange={handlePersonal}
              placeholder="City, Country"
              className="form-input"
            />

            <div style={{ marginTop: "15px" }}>
              <label className="form-label">
                Custom Links (LinkedIn, GitHub, Portfolio)
              </label>
              {(formData.personal.customLinks || []).map((link, idx) => (
                <div key={idx} className="custom-link-row">
                  <input
                    type="text"
                    value={link.label}
                    onChange={(e) =>
                      updateCustomLink(idx, "label", e.target.value)
                    }
                    placeholder="Label"
                    className="custom-link-input-label form-input"
                  />
                  <input
                    type="text"
                    value={link.url}
                    onChange={(e) =>
                      updateCustomLink(idx, "url", e.target.value)
                    }
                    placeholder="URL"
                    className="custom-link-input-url form-input"
                  />
                  <button
                    className="icon-btn action-icon-btn"
                    onClick={() => removeCustomLink(idx)}
                  >
                    🗑️
                  </button>
                </div>
              ))}
              <button className="add-btn small mt-1" onClick={addCustomLink}>
                + Add Link
              </button>
            </div>
          </div>
        </div>

        {draggableSections.map((sectionId, index) => {
          const isOpen = expandedSections.includes(sectionId);
          return (
            <div
              key={sectionId}
              id={`accordion-${sectionId}`}
              className={`accordion ${isOpen ? "open" : ""}`}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              style={{
                opacity: draggedItemIndex === index ? 0.5 : 1,
                transition: "opacity 0.2s",
              }}
            >
              <div
                className="accordion-header"
                onClick={() => toggleAccordion(sectionId)}
              >
                <div className="accordion-title-container">
                  <span className="drag-handle" title="Drag to reorder">
                    ⋮⋮
                  </span>
                  <span>
                    {sectionConfig[sectionId]?.icon}{" "}
                    {sectionConfig[sectionId]?.title}
                  </span>
                </div>
                <span className="chevron">{isOpen ? "▲" : "▼"}</span>
              </div>

              <div className="accordion-body">
                {sectionId === "summary" && (
                  <>
                    <div className="section-header-row">
                      <span className="form-label" style={{ margin: 0 }}>
                        Overview
                      </span>
                      <button
                        onClick={handleAutoWriteSummary}
                        disabled={isGenerating}
                        className="ai-generate-btn"
                      >
                        {isGenerating ? "⏳ Generating..." : "✨ AI Auto-Write"}
                      </button>
                    </div>
                    <textarea
                      rows="5"
                      className="form-textarea"
                      value={formData.summary}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          summary: e.target.value,
                        }))
                      }
                      placeholder="Write a compelling hook..."
                    />
                  </>
                )}

                {sectionId === "experience" && (
                  <>
                    <div className="scrollable-list custom-scrollbar">
                      {formData.experience.map((exp) => (
                        <div key={exp.id} className="item-card">
                          <div>
                            <strong className="item-title">
                              {exp.company || "New Position"}
                            </strong>
                            <span className="item-subtitle">
                              {exp.role || "Job Title"}
                            </span>
                          </div>
                          <div className="item-actions">
                            <button
                              className="icon-btn action-icon-btn"
                              onClick={() => setEditingExpId(exp.id)}
                              title="Edit Experience"
                            >
                              ✏️
                            </button>
                            <button
                              className="icon-btn action-icon-btn"
                              onClick={() => removeExperience(exp.id)}
                              title="Delete Experience"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button className="add-btn mt-3" onClick={addExperience}>
                      + Add Experience
                    </button>
                  </>
                )}

                {sectionId === "education" && (
                  <>
                    <div className="scrollable-list short custom-scrollbar">
                      {formData.education.map((ed) => (
                        <div key={ed.id} className="item-card column-layout">
                          <div
                            className="item-card-header section-header-row"
                            style={{ width: "100%" }}
                          >
                            <strong>
                              {ed.school ||
                                (ed.type === "school"
                                  ? "New School"
                                  : "New University")}
                            </strong>
                            <button
                              className="icon-btn action-icon-btn"
                              onClick={() => removeEducation(ed.id)}
                            >
                              🗑️
                            </button>
                          </div>

                          <select
                            className="form-select"
                            value={ed.type || "university"}
                            onChange={(e) =>
                              updateEducation(ed.id, "type", e.target.value)
                            }
                          >
                            <option value="university">University</option>
                            <option value="school">School / College</option>
                          </select>

                          <input
                            type="text"
                            className="form-input"
                            value={ed.school}
                            onChange={(e) =>
                              updateEducation(ed.id, "school", e.target.value)
                            }
                            placeholder={
                              ed.type === "school"
                                ? "School / College Name"
                                : "University Name"
                            }
                          />

                          {ed.type === "school" ? (
                            <textarea
                              rows="2"
                              className="form-textarea"
                              value={ed.qualifications || ""}
                              onChange={(e) =>
                                updateEducation(
                                  ed.id,
                                  "qualifications",
                                  e.target.value,
                                )
                              }
                              placeholder="Qualifications (e.g., 3 A-Levels: Maths (A), Physics (B)...)"
                            />
                          ) : (
                            <input
                              type="text"
                              className="form-input"
                              value={ed.degree}
                              onChange={(e) =>
                                updateEducation(ed.id, "degree", e.target.value)
                              }
                              placeholder="Degree (e.g., BSc Computer Science)"
                            />
                          )}

                          <input
                            type="text"
                            className="form-input"
                            value={ed.year}
                            onChange={(e) =>
                              updateEducation(ed.id, "year", e.target.value)
                            }
                            placeholder={
                              ed.type === "school"
                                ? "Years Attended (e.g., 2018 - 2020)"
                                : "Graduation Year"
                            }
                          />
                        </div>
                      ))}
                    </div>
                    <button className="add-btn mt-3" onClick={addEducation}>
                      + Add Education
                    </button>
                  </>
                )}

                {sectionId === "projects" && (
                  <>
                    <div className="scrollable-list short custom-scrollbar">
                      {formData.projects.map((pr) => (
                        <div key={pr.id} className="item-card column-layout">
                          <div
                            className="item-card-header section-header-row"
                            style={{ width: "100%" }}
                          >
                            <strong>{pr.title || "New Project"}</strong>
                            <button
                              className="icon-btn action-icon-btn"
                              onClick={() => removeProject(pr.id)}
                            >
                              🗑️
                            </button>
                          </div>
                          <input
                            type="text"
                            className="form-input"
                            value={pr.title}
                            onChange={(e) =>
                              updateProject(pr.id, "title", e.target.value)
                            }
                            placeholder="Project Title"
                          />
                          <input
                            type="text"
                            className="form-input"
                            value={pr.link}
                            onChange={(e) =>
                              updateProject(pr.id, "link", e.target.value)
                            }
                            placeholder="Link (e.g., GitHub)"
                          />
                          <textarea
                            rows="3"
                            className="form-textarea"
                            value={pr.desc}
                            onChange={(e) =>
                              updateProject(pr.id, "desc", e.target.value)
                            }
                            placeholder="Project description..."
                          />
                        </div>
                      ))}
                    </div>
                    <button className="add-btn mt-3" onClick={addProject}>
                      + Add Project
                    </button>
                  </>
                )}

                {sectionId === "skills" && (
                  <div className="scrollable-list short custom-scrollbar">
                    {formData.skills.map((skill, index) => (
                      <div key={index} className="skill-row">
                        <input
                          type="text"
                          className="skill-input form-input"
                          value={skill}
                          onChange={(e) =>
                            handleSkillChange(index, e.target.value)
                          }
                          placeholder="e.g., React, Node.js"
                        />
                        {formData.skills.length > 1 &&
                          index !== formData.skills.length - 1 && (
                            <button
                              className="icon-btn action-icon-btn"
                              onClick={() => removeSkill(index)}
                            >
                              🗑️
                            </button>
                          )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderDesignPanel = () => {
    const templates = [
      { id: "nova", name: "Nova" },
      { id: "elevate", name: "Elevate" },
      { id: "signature", name: "Signature" },
      { id: "metric", name: "Metric" },
      { id: "chronos", name: "Chronos" },
      { id: "clarity", name: "Clarity" },
      { id: "syntax", name: "Syntax" },
      { id: "kinetic", name: "Kinetic" },
      { id: "editorial", name: "Editorial" },
      { id: "forge", name: "Forge" },
      { id: "aura", name: "Aura" },
      { id: "pinnacle", name: "Pinnacle" },
    ];

    const accentColors = [
      "#0f172a",
      "#334155",
      "#2563eb",
      "#4f46e5",
      "#059669",
      "#0f766e",
      "#9f1239",
      "#b45309",
      "#be185d",
    ];
    const headingColors = [
      "#000000",
      "#0f172a",
      "#1e293b",
      "#334155",
      "#1e3a8a",
      "#064e3b",
      "#7f1d1d",
    ];
    const textColors = ["#000000", "#1e293b", "#334155", "#475569", "#4b5563"];
    const subtitleColors = [
      "#475569",
      "#64748b",
      "#94a3b8",
      "#71717a",
      "#6b7280",
    ];
    const bgColors = ["#ffffff", "#f8fafc", "#fdfbf7", "#f3f4f6", "#fffbeb"];

    return (
      <div className="panel-content fade-in" style={{ paddingBottom: "40px" }}>
        <div className="panel-header">
          <h2>Design Studio</h2>
          <p>Customise your layout, colours, and typography.</p>
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginTop: "12px",
              alignItems: "center",
            }}
          >
            <span
              style={{
                fontSize: "0.85rem",
                color: "#64748b",
                fontWeight: "600",
              }}
            >
              History:
            </span>
            <button
              onClick={handleUndo}
              disabled={past.length === 0}
              className="add-btn small"
              style={{
                opacity: past.length === 0 ? 0.5 : 1,
                background: "#f8fafc",
                color: "#0f172a",
                border: "1px solid #cbd5e1",
                padding: "4px 10px",
              }}
            >
              ↩️ Undo
            </button>
            <button
              onClick={handleRedo}
              disabled={future.length === 0}
              className="add-btn small"
              style={{
                opacity: future.length === 0 ? 0.5 : 1,
                background: "#f8fafc",
                color: "#0f172a",
                border: "1px solid #cbd5e1",
                padding: "4px 10px",
              }}
            >
              ↪️ Redo
            </button>
          </div>
        </div>

        <div
          className="design-sub-nav"
          style={{
            display: "flex",
            gap: "8px",
            marginBottom: "20px",
            borderBottom: "1px solid #e2e8f0",
            paddingBottom: "10px",
            overflowX: "auto",
          }}
        >
          {["templates", "colors", "typography", "layout"].map((tab) => (
            <button
              key={tab}
              onClick={() => setDesignSubTab(tab)}
              style={{
                padding: "6px 12px",
                borderRadius: "6px",
                background: designSubTab === tab ? "#2563eb" : "transparent",
                color: designSubTab === tab ? "#fff" : "#64748b",
                fontWeight: designSubTab === tab ? "600" : "400",
                border: "none",
                cursor: "pointer",
                textTransform: "capitalize",
                transition: "all 0.2s",
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {designSubTab === "templates" && (
          <div className="design-group fade-in">
            <label>Template</label>
            <div className="template-grid-container">
              {templates.map((tpl) => (
                <div
                  key={tpl.id}
                  className={`temp-box ${design.theme === tpl.id ? "active" : ""}`}
                  onClick={() => handleDesign("theme", tpl.id)}
                >
                  {tpl.name}
                </div>
              ))}
            </div>
          </div>
        )}

        {designSubTab === "colors" && (
          <div className="fade-in">
            <ColorPickerRow
              label="Accent Theme Colour"
              propName="color"
              palette={accentColors}
              currentValue={design.color}
              onColorChange={handleDesign}
            />
            <ColorPickerRow
              label="Heading Text Colour"
              propName="headingColor"
              palette={headingColors}
              currentValue={design.headingColor || "#0f172a"}
              onColorChange={handleDesign}
            />
            <ColorPickerRow
              label="Body Text Colour"
              propName="textColor"
              palette={textColors}
              currentValue={design.textColor || "#334155"}
              onColorChange={handleDesign}
            />
            <ColorPickerRow
              label="Dates & Subtitles Colour"
              propName="subtitleColor"
              palette={subtitleColors}
              currentValue={design.subtitleColor || "#64748b"}
              onColorChange={handleDesign}
            />
            <ColorPickerRow
              label="Background Colour"
              propName="backgroundColor"
              palette={bgColors}
              currentValue={design.backgroundColor}
              onColorChange={handleDesign}
            />
          </div>
        )}

        {designSubTab === "typography" && (
          <div className="fade-in">
            <div className="design-group">
              <label>Font Family</label>
              <select
                className="form-select"
                value={design.font}
                onChange={(e) => handleDesign("font", e.target.value)}
              >
                <optgroup label="Modern Sans-Serif">
                  <option value="Inter">Inter</option>
                  <option value="Manrope">Manrope</option>
                  <option value="Plus Jakarta Sans">Plus Jakarta</option>
                  <option value="Outfit">Outfit</option>
                  <option value="Space Grotesk">Space Grotesk</option>
                  <option value="Roboto">Roboto</option>
                </optgroup>
                <optgroup label="Elegant Serif">
                  <option value="Merriweather">Merriweather</option>
                  <option value="Lora">Lora</option>
                  <option value="Crimson Text">Crimson Text</option>
                  <option value="Cormorant Garamond">Cormorant</option>
                </optgroup>
              </select>
            </div>

            <div className="design-group design-group-highlight">
              <label
                className="design-range-label first"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "15px",
                  marginBottom: "8px",
                }}
              >
                <span>Base Font Size</span>
                <span className="design-range-value">{design.fontSize}pt</span>
              </label>
              <input
                type="range"
                className="design-range-input"
                min="7"
                max="12"
                step="0.5"
                value={design.fontSize}
                onChange={(e) =>
                  handleDesign("fontSize", parseFloat(e.target.value))
                }
              />

              <label
                className="design-range-label"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "15px",
                  marginBottom: "8px",
                  marginTop: "15px",
                }}
              >
                <span>Line Height</span>
                <span className="design-range-value">
                  {design.lineHeight || 1.5}
                </span>
              </label>
              <input
                type="range"
                className="design-range-input"
                min="1.1"
                max="2.0"
                step="0.1"
                value={design.lineHeight || 1.5}
                onChange={(e) =>
                  handleDesign("lineHeight", parseFloat(e.target.value))
                }
              />
            </div>

            <div className="design-group">
              <label>Section Titles Casing</label>
              <div className="alignment-btn-group">
                {["uppercase", "capitalize", "none"].map((caseType) => (
                  <button
                    key={caseType}
                    className="alignment-btn"
                    onClick={() => handleDesign("textTransform", caseType)}
                    style={{
                      background:
                        (design.textTransform || "uppercase") === caseType
                          ? "#2563eb"
                          : "#f1f5f9",
                      color:
                        (design.textTransform || "uppercase") === caseType
                          ? "white"
                          : "#475569",
                      textTransform: caseType === "none" ? "initial" : caseType,
                    }}
                  >
                    {caseType === "none" ? "Normal" : caseType}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {designSubTab === "layout" && (
          <div className="fade-in">
            <div className="design-group design-group-highlight">
              <label
                className="design-range-label first"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "15px",
                  marginBottom: "8px",
                }}
              >
                <span>Document Padding</span>
                <span className="design-range-value">{design.padding}px</span>
              </label>
              <input
                type="range"
                className="design-range-input"
                min="10"
                max="60"
                step="2"
                value={design.padding}
                onChange={(e) =>
                  handleDesign("padding", parseInt(e.target.value))
                }
              />

              <label
                className="design-range-label"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "15px",
                  marginBottom: "8px",
                  marginTop: "15px",
                }}
              >
                <span>Section Spacing</span>
                <span className="design-range-value">
                  {design.sectionSpacing}px
                </span>
              </label>
              <input
                type="range"
                className="design-range-input"
                min="4"
                max="30"
                step="2"
                value={design.sectionSpacing}
                onChange={(e) =>
                  handleDesign("sectionSpacing", parseInt(e.target.value))
                }
              />

              <label
                className="design-range-label"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "15px",
                  marginBottom: "8px",
                  marginTop: "15px",
                }}
              >
                <span>Item Spacing</span>
                <span className="design-range-value">
                  {design.itemSpacing}px
                </span>
              </label>
              <input
                type="range"
                className="design-range-input"
                min="2"
                max="20"
                step="2"
                value={design.itemSpacing}
                onChange={(e) =>
                  handleDesign("itemSpacing", parseInt(e.target.value))
                }
              />
            </div>

            <div className="design-group">
              <label>Header Alignment Override</label>
              <div className="alignment-btn-group">
                {["default", "left", "center", "right"].map((align) => (
                  <button
                    key={align}
                    className="alignment-btn"
                    onClick={() => handleDesign("headerAlign", align)}
                    style={{
                      background:
                        design.headerAlign === align ? "#2563eb" : "#f1f5f9",
                      color: design.headerAlign === align ? "white" : "#475569",
                    }}
                  >
                    {align}
                  </button>
                ))}
              </div>
            </div>

            <div
              className="design-group"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "15px",
              }}
            >
              <label style={{ margin: 0 }}>Show Section Dividers</label>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={design.showDividers !== false}
                  onChange={(e) =>
                    handleDesign("showDividers", e.target.checked)
                  }
                />
                <span className="slider round"></span>
              </label>
            </div>
          </div>
        )}
      </div>
    );
  };

  // --- ACTIONS & EXPORT PANEL ---
  const renderActionsPanel = () => {
    return (
      <div className="panel-content fade-in">
        <div className="panel-header">
          <h2>Actions & Export</h2>
          <p>Manage your CV file and check ATS compatibility.</p>
        </div>

        <div className="design-group">
          <label>Document Status</label>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "10px" }}
          >
            <div
              className="status-badge"
              title="Estimated Applicant Tracking System (ATS) completeness score based on key sections."
              style={{
                background:
                  displayAtsScore >= 80
                    ? "#f0fdf4"
                    : displayAtsScore >= 60
                      ? "#fffbeb"
                      : "#fef2f2",
                color:
                  displayAtsScore >= 80
                    ? "#166534"
                    : displayAtsScore >= 60
                      ? "#92400e"
                      : "#991b1b",
                border: `1px solid ${
                  displayAtsScore >= 80
                    ? "#bbf7d0"
                    : displayAtsScore >= 60
                      ? "#fde68a"
                      : "#fecaca"
                }`,
                textAlign: "center",
                padding: "10px",
                borderRadius: "6px",
                fontWeight: "600",
              }}
            >
              🤖 ATS Score: {displayAtsScore}%
            </div>

            {/* Actionable ATS Fixes List */}
            {atsAnalysis.fixes.length > 0 ? (
              <div
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  padding: "10px 12px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                }}
              >
                <span
                  style={{
                    fontSize: "0.78rem",
                    fontWeight: "600",
                    color: "#475569",
                    marginBottom: "2px",
                  }}
                >
                  Quick fixes to boost your score:
                </span>
                {atsAnalysis.fixes.map((fix) => (
                  <button
                    key={fix.id}
                    type="button"
                    onClick={() => jumpToSection(fix.section)}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                      background: "#ffffff",
                      border: "1px solid #cbd5e1",
                      borderRadius: "6px",
                      padding: "7px 10px",
                      fontSize: "0.82rem",
                      color: "#0f172a",
                      cursor: "pointer",
                      textAlign: "left",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <span>→ {fix.label}</span>
                    <strong style={{ color: "#16a34a", marginLeft: "8px" }}>
                      {fix.boost}
                    </strong>
                  </button>
                ))}
              </div>
            ) : (
              <div
                style={{
                  fontSize: "0.8rem",
                  color: "#166534",
                  textAlign: "center",
                }}
              >
                ✓ All core ATS sections are complete!
              </div>
            )}

            <div
              className="status-badge"
              style={{
                background: isPro
                  ? "#eff6ff"
                  : pdfCredits > 0
                    ? "#f0fdf4"
                    : "#fef2f2",
                color: isPro
                  ? "#1d4ed8"
                  : pdfCredits > 0
                    ? "#166534"
                    : "#991b1b",
                border: `1px solid ${
                  isPro ? "#bfdbfe" : pdfCredits > 0 ? "#bbf7d0" : "#fecaca"
                }`,
                textAlign: "center",
                padding: "8px",
                borderRadius: "6px",
              }}
            >
              {isPro
                ? "💎 Pro Plan Active"
                : `🎟️ Credits Remaining: ${pdfCredits}`}
            </div>
          </div>
          {resumeId && (
            <div
              className="save-status-text"
              style={{ marginTop: "12px", justifyContent: "center" }}
            >
              {saveStatus}
            </div>
          )}
        </div>

        <div className="design-group" style={{ marginTop: "30px" }}>
          <label>File Actions</label>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            <button
              className="export-btn"
              style={{
                width: "100%",
                padding: "14px",
                fontSize: "1rem",
                borderRadius: "8px",
              }}
              onClick={handleDownloadClick}
            >
              ⬇ Download PDF
            </button>

            <button
              className="export-btn clear-cv-btn"
              style={{
                width: "100%",
                padding: "14px",
                fontSize: "1rem",
                borderRadius: "8px",
                background: "#fff1f2",
                color: "#e11d48",
                border: "1px solid #fecdd3",
                cursor: "pointer",
                fontWeight: "500",
                transition: "all 0.2s ease",
              }}
              onClick={handleClearCVClick}
            >
              🗑️ Clear CV Content
            </button>

            <label
              className="export-btn import-btn"
              style={{
                textAlign: "center",
                width: "100%",
                padding: "14px",
                fontSize: "1rem",
                margin: 0,
                cursor: "pointer",
                borderRadius: "8px",
                background: "#f1f5f9",
                color: "#1e293b",
              }}
            >
              📥 Import Existing CV
              <input
                type="file"
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                style={{ display: "none" }}
                onChange={handleImportExternalCV}
              />
            </label>

            <button
              className="export-btn save-dashboard-btn"
              style={{
                width: "100%",
                padding: "14px",
                fontSize: "1rem",
                borderRadius: "8px",
                background: "#0f172a",
                color: "#ffffff",
                opacity: !isSavingPending && resumeId ? 0.6 : 1,
                pointerEvents: !isSavingPending && resumeId ? "none" : "auto",
              }}
              onClick={() => initiateSaveToCloud("save")}
              disabled={!isSavingPending && resumeId}
            >
              {!isSavingPending && resumeId
                ? "✓ Saved to Dashboard"
                : "☁️ Save to Dashboard"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // --- DYNAMIC STYLES ---
  let activeAlign = design.headerAlign;
  if (!activeAlign || activeAlign === "default") {
    activeAlign = design.theme === "prism" ? "center" : "left";
  }

  const headerFlexAlign =
    activeAlign === "center"
      ? "center"
      : activeAlign === "right"
        ? "flex-end"
        : "flex-start";

  const headerAlignOverride = `
    .a4-document .res-header-text,
    .a4-document .res-header h1,
    .a4-document .res-header .res-role {
        text-align: ${activeAlign} !important;
        align-items: ${headerFlexAlign} !important;
        justify-content: ${headerFlexAlign} !important;
    }
    
    .a4-document .res-contact {
        text-align: ${activeAlign} !important;
        align-items: ${headerFlexAlign} !important;
    }
  `;

  const dividersCSS =
    design.showDividers === false
      ? `
    .a4-document hr, 
    .a4-document .section-divider { display: none !important; }
    .a4-document .res-section-title { border-bottom: none !important; }
    .a4-document .res-section-title::after { display: none !important; }
    .a4-document.elegant .res-header { border-bottom: none !important; }
    .a4-document.executive .res-header { border-bottom: none !important; }
    .a4-document.startup .res-section-title { border-left: none !important; }
    .a4-document.academic .res-header { border-bottom: none !important; }
    .a4-document.academic .res-section-title { border-bottom: none !important; }
    .a4-document.creative { border-top: none !important; }
  `
      : `
    .a4-document hr, 
    .a4-document .section-divider { 
      display: block !important; 
      border-color: ${design.color} !important; 
      opacity: 0.3; 
    }
  `;

  const isSerif = [
    "Merriweather",
    "Lora",
    "Crimson Text",
    "Cormorant Garamond",
  ].includes(design.font);
  const fontStack = `"${design.font}", ${isSerif ? "serif" : "sans-serif"}`;

  const dynamicStyles = `
    .a4-document {
      overflow: visible !important;
      background: transparent !important; 
      box-shadow: none !important;        
    }

    .a4-document, 
    .a4-document *,
    .a4-document h1,
    .a4-document h2,
    .a4-document h3,
    .a4-document p,
    .a4-document span,
    .a4-document li,
    .a4-document .res-section-title,
    .a4-document .job-title,
    .a4-document .job-company,
    .a4-document .res-skill-badge {
      font-family: ${fontStack} !important;
    }

    .a4-document, 
    .a4-document * {
      color: ${design.textColor || "#334155"} !important;
      line-height: ${design.lineHeight || 1.5} !important;
    }
    
    .a4-document h1, 
    .a4-document h2, 
    .a4-document h3, 
    .a4-document .res-section-title {
      color: ${design.headingColor || "#0f172a"} !important;
    }

    .a4-document .res-section-title {
      text-transform: ${design.textTransform || "uppercase"} !important;
    }

    .a4-document .item-subtitle, 
    .a4-document .job-date,
    .a4-document .res-contact,
    .a4-document .res-contact *,
    .a4-document .header-links a {
      color: ${design.subtitleColor || "#64748b"} !important;
    }

    ${dividersCSS}

    ${headerAlignOverride}
  `;

  if (isLoadingAuth) {
    return (
      <div className="auth-loading-screen">
        <h2 className="auth-loading-text">Verifying access...</h2>
      </div>
    );
  }

  if (!isLoggedIn) return <Navigate to="/auth" replace />;

  if (isExporting) {
    return (
      <>
        <style>{dynamicStyles}</style>
        <div
          className="print-only print-only-wrapper"
          data-align={design.headerAlign}
          style={{
            "--doc-font-size": `${design.fontSize}pt`,
            "--doc-line-height": `${design.lineHeight || 1.5}`,
            "--doc-text-color": design.textColor || "#334155",
            "--doc-heading-color": design.headingColor || "#0f172a",
            "--doc-subtitle-color": design.subtitleColor || "#64748b",
            "--doc-text-transform": design.textTransform || "uppercase",
            "--doc-padding-v": `${design.padding}px`,
            "--doc-padding-h": `${design.padding + 10}px`,
            "--doc-section-spacing": `${design.sectionSpacing}px`,
            "--doc-item-spacing": `${design.itemSpacing}px`,
            "--doc-bg-color": design.backgroundColor,
          }}
        >
          <ResumeDocument formData={formData} design={design} />
        </div>
      </>
    );
  }

  const activeExp = editingExpId
    ? formData.experience.find((e) => e.id === editingExpId)
    : null;

  return (
    <>
      <Helmet>
        <title>Resumify | Builder</title>
        <meta
          name="description"
          content="Create your ATS-optimized CVs using our intelligent builder. Tailor your experience, generate AI summaries, and design a standout layout."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <style>{dynamicStyles}</style>

      <Toaster
        position={isMobileMode ? "bottom-center" : "bottom-right"}
        containerStyle={{
          bottom: isMobileMode ? "85px" : "20px",
          zIndex: 99999,
        }}
      />

      <Navbar />

      <div className="builder-layout">
        <div className="workspace">
          <nav
            className={`tool-dock ${mobileView === "preview" ? "mobile-hidden" : ""}`}
          >
            <button
              className={`dock-btn ${activeTab === "content" ? "active" : ""}`}
              onClick={() => setActiveTab("content")}
              title="Content"
            >
              📄
            </button>
            <button
              className={`dock-btn ${activeTab === "design" ? "active" : ""}`}
              onClick={() => setActiveTab("design")}
              title="Design"
            >
              🎨
            </button>
            <button
              className={`dock-btn ${activeTab === "actions" ? "active" : ""}`}
              onClick={() => setActiveTab("actions")}
              title="Actions & Export"
            >
              ⚡
            </button>
          </nav>

          <aside
            className={`control-panel custom-scrollbar ${mobileView === "preview" ? "mobile-hidden" : ""}`}
          >
            {activeTab === "content" && renderContentPanel()}
            {activeTab === "design" && renderDesignPanel()}
            {activeTab === "actions" && renderActionsPanel()}
          </aside>

          <main
            className={`canvas-area custom-scrollbar ${mobileView === "edit" ? "mobile-hidden" : ""}`}
          >
            <div
              className="canvas-centerer"
              data-align={design.headerAlign}
              style={{
                paddingTop: isMobileMode ? "30px" : "32px",
                paddingBottom: isMobileMode ? "100px" : "40px",
                paddingLeft: isMobileMode ? "20px" : "40px",
                paddingRight: isMobileMode ? "20px" : "40px",
                "--doc-font-size": `${design.fontSize}pt`,
                "--doc-line-height": `${design.lineHeight || 1.5}`,
                "--doc-text-color": design.textColor || "#334155",
                "--doc-heading-color": design.headingColor || "#0f172a",
                "--doc-subtitle-color": design.subtitleColor || "#64748b",
                "--doc-text-transform": design.textTransform || "uppercase",
                "--doc-padding-v": `${design.padding}px`,
                "--doc-padding-h": `${design.padding + 10}px`,
                "--doc-section-spacing": `${design.sectionSpacing}px`,
                "--doc-item-spacing": `${design.itemSpacing}px`,
                "--doc-bg-color": "transparent",
              }}
            >
              {!isExporting && (
                <div
                  className="mobile-height-constrainer"
                  style={{
                    width: `${800 * (zoom / 100)}px`,
                    height: `${docHeight * (zoom / 100)}px`,
                    margin: "0 auto",
                    position: "relative",
                    transition: "height 0.2s ease",
                  }}
                >
                  <div
                    className="scale-wrapper screen-only"
                    style={{
                      width: "800px",
                      height: `${docHeight}px`,
                      transform: `scale(${zoom / 100})`,
                      transformOrigin: "top left",
                      position: "absolute",
                      top: 0,
                      left: 0,
                      overflow: "visible",
                    }}
                  >
                    <div
                      className="doc-shadow-wrapper"
                      style={{
                        height: `${docHeight}px`,
                        overflow: "visible",
                        background: `repeating-linear-gradient(
                          to bottom,
                          ${design.backgroundColor || "#ffffff"} 0px,
                          ${design.backgroundColor || "#ffffff"} 1131px,
                          transparent 1131px,
                          transparent 1171px
                        )`,
                        boxShadow: "none",
                        filter:
                          "drop-shadow(0 10px 15px rgba(0,0,0,0.1)) drop-shadow(0 4px 6px rgba(0,0,0,0.05))",
                      }}
                    >
                      <div
                        className="visual-doc-wrapper"
                        style={{
                          height: `${docHeight}px`,
                          overflow: "visible",
                        }}
                      >
                        <ResumeDocument formData={formData} design={design} />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </main>

          <div className="mobile-view-toggle">
            <button
              className={mobileView === "edit" ? "active" : ""}
              onClick={() => setMobileView("edit")}
            >
              ✏️ Editor
            </button>
            <button
              className={mobileView === "preview" ? "active" : ""}
              onClick={() => setMobileView("preview")}
            >
              👁️ Preview
            </button>
          </div>
        </div>
      </div>

      {/* --- EXPERIENCE EDIT MODAL --- */}
      {editingExpId && activeExp && (
        <div className="modal-overlay z-3000 fade-in">
          <div className="modal-content custom-scrollbar">
            <div className="modal-header">
              <h2 className="modal-title">Edit Experience</h2>
              <button
                className="icon-btn modal-close-btn"
                onClick={() => setEditingExpId(null)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div>
                <label className="modal-label">Company</label>
                <textarea
                  rows="1"
                  className="modal-input"
                  value={activeExp.company}
                  onChange={(e) =>
                    updateExperience(activeExp.id, "company", e.target.value)
                  }
                  onInput={handleAutoResize}
                  ref={handleRefResize}
                  style={{ resize: "none", overflow: "hidden" }}
                />
              </div>
              <div>
                <label className="modal-label">Job Title</label>
                <textarea
                  rows="1"
                  className="modal-input"
                  value={activeExp.role}
                  onChange={(e) =>
                    updateExperience(activeExp.id, "role", e.target.value)
                  }
                  onInput={handleAutoResize}
                  ref={handleRefResize}
                  style={{ resize: "none", overflow: "hidden" }}
                />
              </div>
              <div>
                <label className="modal-label">Dates</label>
                <textarea
                  rows="1"
                  className="modal-input"
                  value={activeExp.dates}
                  onChange={(e) =>
                    updateExperience(activeExp.id, "dates", e.target.value)
                  }
                  onInput={handleAutoResize}
                  ref={handleRefResize}
                  style={{ resize: "none", overflow: "hidden" }}
                  placeholder="e.g., Jan 2021 - Present"
                />
              </div>

              <div style={{ marginTop: "10px" }}>
                <div className="section-header-row">
                  <label className="modal-label" style={{ margin: 0 }}>
                    Responsibilities / Achievements
                  </label>
                  <button
                    onClick={() =>
                      handleOptimizeBullets(activeExp.id, activeExp)
                    }
                    disabled={isGenerating}
                    className="ai-generate-btn"
                  >
                    {isGenerating ? "⏳ Processing..." : "✨ Smart Optimize"}
                  </button>
                </div>

                {(Array.isArray(activeExp.desc)
                  ? activeExp.desc
                  : [activeExp.desc || ""]
                ).map((bullet, index) => (
                  <div key={index} className="bullet-action-row">
                    <textarea
                      rows="1"
                      className="modal-textarea"
                      value={bullet}
                      onChange={(e) =>
                        handleExpBulletChange(
                          activeExp.id,
                          index,
                          e.target.value,
                        )
                      }
                      onInput={handleAutoResize}
                      ref={handleRefResize}
                      style={{ resize: "none", overflow: "hidden" }}
                      placeholder="e.g., Led the migration of legacy architecture..."
                    />
                    {(Array.isArray(activeExp.desc) ? activeExp.desc : [""])
                      .length > 1 &&
                      index !==
                        (Array.isArray(activeExp.desc) ? activeExp.desc : [""])
                          .length -
                          1 && (
                        <button
                          className="icon-btn action-icon-btn"
                          onClick={() => removeExpBullet(activeExp.id, index)}
                          style={{ marginTop: "5px" }}
                        >
                          🗑️
                        </button>
                      )}
                  </div>
                ))}
              </div>
            </div>

            <button
              className="modal-submit-btn"
              onClick={() => setEditingExpId(null)}
            >
              Done Editing
            </button>
          </div>
        </div>
      )}

      {/* --- SAVE MODAL --- */}
      {showSaveModal && (
        <div className="modal-overlay z-1000 fade-in">
          <div className="modal-content small-modal">
            <h2 className="modal-title center">Name Your CV</h2>
            <input
              type="text"
              className="modal-input large"
              value={resumeTitle}
              onChange={(e) => setResumeTitle(e.target.value)}
              placeholder="e.g., Software Engineer - Google"
              autoFocus
            />
            <div className="modal-btn-group row">
              <button
                className="modal-btn cancel-outlined"
                onClick={() => setShowSaveModal(false)}
              >
                Cancel
              </button>
              <button
                className="modal-btn primary"
                onClick={confirmSaveToCloud}
              >
                {saveAction === "exit" ? "Save & Exit" : "Save to Cloud"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- DOWNLOAD CONFIRM MODAL --- */}
      {showDownloadConfirm && (
        <div className="modal-overlay z-1000 fade-in">
          <div className="modal-content small-modal">
            <h2 className="modal-title center">Ready to Download?</h2>
            <p className="modal-desc">
              Downloading this PDF will consume{" "}
              <strong className="modal-highlight">1</strong> of your{" "}
              {pdfCredits} remaining PDF credits.
            </p>
            <div className="modal-btn-group">
              <button className="modal-btn primary" onClick={executeDownload}>
                Yes, Download PDF
              </button>
              <button
                className="modal-btn cancel"
                onClick={() => setShowDownloadConfirm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- UPGRADE MODAL --- */}
      {upgradeModal.show && (
        <div className="modal-overlay z-1000 fade-in">
          <div className="modal-content small-modal">
            <h2 className="modal-title center">{upgradeModal.title}</h2>
            <p className="modal-desc">
              You currently have{" "}
              <strong className="modal-highlight">{pdfCredits}</strong> credits.{" "}
              {upgradeModal.desc}
            </p>
            <div className="modal-btn-group">
              <button
                className="modal-btn success"
                onClick={() => handleCheckout("single_pass")}
              >
                Buy 1 PDF Credit (£4.99)
              </button>
              <button
                className="modal-btn cancel"
                onClick={() =>
                  setUpgradeModal({ ...upgradeModal, show: false })
                }
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- CUSTOM ALERT MODAL --- */}
      {customAlert.show && (
        <div
          className="modal-overlay z-9999 fade-in"
          onClick={() =>
            setCustomAlert({ show: false, title: "", message: "" })
          }
        >
          <div
            className="modal-content small-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="alert-icon">⚠️</div>
            <h3 className="modal-title center">{customAlert.title}</h3>
            <p className="modal-desc">{customAlert.message}</p>
            <button
              className="modal-submit-btn"
              style={{ marginTop: 0 }}
              onClick={() =>
                setCustomAlert({ show: false, title: "", message: "" })
              }
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* --- SUBSCRIPTION LIMIT MODAL --- */}
      {showSubscribeModal && (
        <div className="modal-overlay z-1000 fade-in">
          <div className="modal-content small-modal">
            <h2 className="modal-title center">Save Limit Reached</h2>
            <p className="modal-desc">
              You have reached the maximum limit of 3 free CVs. Please subscribe
              to our Pro plan to save unlimited CVs to your dashboard.
            </p>
            <div className="modal-btn-group">
              <button
                className="modal-btn success"
                onClick={() => {
                  setShowSubscribeModal(false);
                  handleCheckout("pro");
                }}
              >
                Upgrade to Pro
              </button>
              <button
                className="modal-btn cancel"
                onClick={() => setShowSubscribeModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- CLEAR CV CONFIRM MODAL --- */}
      {showClearConfirm && (
        <div className="modal-overlay z-1000 fade-in">
          <div className="modal-content small-modal">
            <div
              className="alert-icon"
              style={{
                fontSize: "2rem",
                textAlign: "center",
                marginBottom: "10px",
              }}
            >
              ⚠️
            </div>
            <h2 className="modal-title center">Clear All Content?</h2>
            <p className="modal-desc" style={{ textAlign: "center" }}>
              Are you sure you want to clear your entire CV? <br />
              <strong className="modal-highlight" style={{ color: "#e11d48" }}>
                This action cannot be undone.
              </strong>
            </p>
            <div className="modal-btn-group">
              <button
                className="modal-btn primary"
                style={{ background: "#e11d48", borderColor: "#e11d48" }}
                onClick={executeClearCV}
              >
                Yes, Clear CV
              </button>
              <button
                className="modal-btn cancel"
                onClick={() => setShowClearConfirm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
